import React from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SortIcon from '@mui/icons-material/Sort';
import {
  FormControl,
  OutlinedInput,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import { Enum } from '@ha/utilities/Enum';
import { track } from '@hbf/analytics';

import { AnalyticsEvent } from '@kamernet/core/Analytics/types';
import {
  RoomAdvertSortOptionID,
  SearchView,
} from '@kamernet/core/ApiClient/Kamernet';
import { useIntl } from '@kamernet/core/Intl';

import { RoomAdvertSortOptionIDTranslations } from '@kamernet/modules/Listing/translations';

import { usePageTitle } from '../../hooks';
import { ListingsSearchContext } from '../../ListingsSearchContext';

import styles from './SortSelect.module.css';

export const SortSelect = () => {
  const { allFilters, applyNewAllFilters } = ListingsSearchContext.useContext();

  const { T } = useIntl();
  const theme = useTheme();
  const isSMDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { pageTitle } = usePageTitle();

  const handleSortChange = React.useCallback(
    (event: SelectChangeEvent<RoomAdvertSortOptionID>) => {
      track(AnalyticsEvent.SearchSortApplied);

      applyNewAllFilters({
        sort: event.target.value as RoomAdvertSortOptionID,
        pageNo: 1,
      });
    },
    [applyNewAllFilters],
  );

  const renderValue = React.useCallback(
    (value: RoomAdvertSortOptionID) => (
      <Typography variant={isSMDown ? 'body3' : 'body2'}>
        {T(RoomAdvertSortOptionIDTranslations[value])}
      </Typography>
    ),
    [T, isSMDown],
  );

  return (
    <div
      className={
        allFilters.searchView === SearchView.Map ? styles.mapGrid : styles.root
      }
    >
      <Typography
        data-testid="listings-count"
        component="span"
        variant="subtitle2"
      >
        {pageTitle}
      </Typography>

      <div className={styles.sortDropdown}>
        <FormControl variant="outlined" size="small">
          <Select<RoomAdvertSortOptionID>
            className={styles.select}
            value={allFilters.sort}
            label={T(
              RoomAdvertSortOptionIDTranslations[
                RoomAdvertSortOptionID.NewestFirst
              ],
            )}
            autoWidth
            size="small"
            input={
              <OutlinedInput
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                }
              />
            }
            renderValue={renderValue}
            IconComponent={KeyboardArrowDownIcon}
            onChange={handleSortChange}
          >
            {Enum.values(RoomAdvertSortOptionID).map(sortOption => (
              <MenuItem key={sortOption} value={sortOption}>
                <Typography variant={isSMDown ? 'body3' : 'body2'}>
                  {T(RoomAdvertSortOptionIDTranslations[sortOption])}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};
